import React, { useEffect, useState } from 'react';
import { ChevronRight, MessageSquarePlus, Loader2 } from 'lucide-react';
import TaxFilingProgressTracker from './TaxFilingProgressTracker';
import { api } from '../lib/api';
import { toast } from 'sonner';

/**
 * Specialist / admin view of a member's tax-filing timeline. Lets the
 * specialist advance to any active stage and post a note.
 */
export default function SpecialistTimelineManager({ memberId, taxYear, onChange }) {
  const [timeline, setTimeline] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [nextStage, setNextStage] = useState('');
  const [note, setNote] = useState('');
  const [noteBody, setNoteBody] = useState('');

  const reload = async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.specialistMemberTimeline(memberId, taxYear),
        api.workflowStages(),
      ]);
      setTimeline(t);
      setStages(s);
      setNextStage(t.current_stage_code || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load timeline');
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [memberId, taxYear]);

  const advance = async () => {
    if (!nextStage) return toast.error('Choose a stage to set');
    setBusy(true);
    try {
      const t = await api.specialistSetMemberStage(memberId, {
        stage_code: nextStage, note: note || '', tax_year: taxYear,
      });
      setTimeline(t);
      setNote('');
      toast.success('Stage updated');
      onChange?.(t);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update stage');
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setBusy(true);
    try {
      const t = await api.specialistAddMemberNote(memberId, {
        body: noteBody.trim(), tax_year: taxYear,
        stage_code: timeline?.current_stage_code,
      });
      setTimeline(t);
      setNoteBody('');
      toast.success('Note added');
      onChange?.(t);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add note');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="specialist-timeline-manager">
      {/* Controls */}
      <div className="bg-white rounded-xl border border-neutral-200 p-5">
        <h3 className="text-[15px] font-bold text-[#14110f] mb-1">Tax filing progress</h3>
        <p className="text-[12.5px] text-neutral-500 mb-4">Advance the timeline and post notes that the member will see.</p>

        <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr_auto] gap-3 items-stretch">
          <select
            data-testid="ts-stage-select"
            value={nextStage}
            onChange={(e) => setNextStage(e.target.value)}
            className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none"
          >
            {stages.map((s) => (
              <option key={s.id} value={s.code}>{`${s.sort_order}. ${s.name}`}</option>
            ))}
          </select>
          <input
            data-testid="ts-stage-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note for the member (sent with key transitions)…"
            className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none"
          />
          <button
            data-testid="ts-stage-save"
            onClick={advance}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-50 text-[#14110f] font-semibold rounded-md text-[13px] px-4 py-2.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Set stage
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-neutral-100">
          <p className="text-[12.5px] uppercase tracking-wider text-neutral-500 font-bold mb-2">Add a note</p>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
            <textarea
              data-testid="ts-note-body"
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              rows={2}
              placeholder="Visible to the member on the timeline…"
              className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13px] focus:border-[#f5b300] outline-none resize-none"
            />
            <button
              data-testid="ts-note-save"
              onClick={addNote}
              disabled={busy || !noteBody.trim()}
              className="inline-flex items-center justify-center gap-2 bg-[#14110f] hover:bg-[#291d12] disabled:opacity-50 text-white font-semibold rounded-md text-[13px] px-4 py-2.5"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" /> Post note
            </button>
          </div>
        </div>
      </div>

      <TaxFilingProgressTracker timeline={timeline} loading={loading} />
    </div>
  );
}
